/**
 * Tab Configuration
 *
 * Defines the tab structure for the Agent Configuration page.
 * All tabs are always visible (no conditional show/hide logic).
 */

import type { AgentConfiguration } from '@/types';

export type TabId =
  | 'basic'
  | 'hooks'
  | 'memory'
  | 'knowledge'
  | 'astrology'
  | 'emotion'
  | 'classifier'
  | 'tools'
  | 'prompts'
  | 'advanced'
  | 'versions';

/** @deprecated - All tabs are now always visible */
export const EXPANDABLE_TABS: TabId[] = [];

export interface TabConfig {
  id: TabId;
  labelKey: string; // i18n key
  icon: string; // Lucide icon name
  order: number;
  /** @deprecated - All tabs are now always visible */
  isVisible?: (config: Partial<AgentConfiguration>) => boolean;
  /** @deprecated - All tabs are now always visible */
  expandable?: boolean;
  /** Description shown in tooltip */
  description?: string;
}

/**
 * Tab configuration
 */
export const TAB_CONFIG: TabConfig[] = [
  {
    id: 'hooks',
    labelKey: 'tabHooks',
    icon: 'Sparkles',
    order: 5,
    description: '功能开关和Hooks管道配置',
  },
  {
    id: 'basic',
    labelKey: 'tabBasic',
    icon: 'Settings',
    order: 10,
    description: '基本信息、模型配置和LLM参数',
  },
  {
    id: 'prompts',
    labelKey: 'tabPrompt',
    icon: 'FileText',
    order: 15,
    description: '系统提示词和模板配置',
  },
  {
    id: 'tools',
    labelKey: 'tabTools',
    icon: 'Wrench',
    order: 18,
    description: '工具配置',
  },
  {
    id: 'memory',
    labelKey: 'tabMemory',
    icon: 'Brain',
    order: 30,
    description: '记忆系统详细配置',
  },
  {
    id: 'knowledge',
    labelKey: 'tabKnowledge',
    icon: 'BookOpen',
    order: 35,
    description: '知识库配置',
  },
  {
    id: 'astrology',
    labelKey: 'tabAstrology',
    icon: 'Star',
    order: 38,
    description: '出生图、本命盘和行运配置',
  },
  {
    id: 'emotion',
    labelKey: 'tabEmotion',
    icon: 'Heart',
    order: 39,
    description: '情绪感知和意图分析配置',
  },
  {
    id: 'classifier',
    labelKey: 'tabClassifier',
    icon: 'Filter',
    order: 16, // Right after prompts (15), before tools (18)
    description: '技能配置',
  },
  {
    id: 'advanced',
    labelKey: 'tabAdvanced',
    icon: 'Cog',
    order: 80,
    description: '高级设置和消息处理',
  },
  {
    id: 'versions',
    labelKey: 'tabVersions',
    icon: 'History',
    order: 100,
    description: '版本历史和JSON配置',
  },
];

/**
 * Get all tabs sorted by order (all tabs are always visible)
 *
 * @param _config - Agent configuration (unused, kept for backwards compatibility)
 * @param _expandedTabs - Expanded tabs set (unused, kept for backwards compatibility)
 */
export function getVisibleTabs(
  _config?: Partial<AgentConfiguration>,
  _expandedTabs?: Set<TabId>
): TabConfig[] {
  // All tabs are always visible, just sort by order
  return [...TAB_CONFIG].sort((a, b) => a.order - b.order);
}

/**
 * Check if a specific tab is visible (always returns true now)
 */
export function isTabVisible(
  tabId: TabId,
  _config?: Partial<AgentConfiguration>,
  _expandedTabs?: Set<TabId>
): boolean {
  const tab = TAB_CONFIG.find(t => t.id === tabId);
  return !!tab; // All tabs are always visible
}

/**
 * Get tab config by ID
 */
export function getTabConfig(tabId: TabId): TabConfig | undefined {
  return TAB_CONFIG.find(t => t.id === tabId);
}

/**
 * @deprecated - All tabs are now always visible, this returns an empty set
 */
export function getInitialExpandedTabs(_config?: Partial<AgentConfiguration>): Set<TabId> {
  return new Set<TabId>();
}

export default TAB_CONFIG;
